/**
 * FailedInspectionsBanner Component
 * Displays a collapsible banner showing failed inspections that can be fixed in bulk
 */

import React, { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import {
	WarningAmber,
	ChevronRight,
	ExpandMore,
	Build,
} from '@mui/icons-material';
import { useFeatureEnabled } from 'Components/FeatureToggle';
import './index.scss';

/**
 * Groups failed inspections by their failure reason
 * @param {Array} failedInspections - Array of inspection objects with failed/failureReason
 * @returns {Array} Array of { reason, inspections, count }
 */
const groupByReason = (failedInspections) => {
	const groups = {};

	failedInspections.forEach((inspection) => {
		const reason = inspection.failureReason || 'Unknown failure reason';
		if (!groups[reason]) {
			groups[reason] = [];
		}
		groups[reason].push(inspection);
	});

	return Object.entries(groups).map(([reason, inspections]) => ({
		reason,
		inspections,
		count: inspections.length,
	}));
};

const FailedInspectionsBanner = ({ inspections, onFixAll }) => {
	const { messages } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	// Feature flag to show/hide the failed inspections banner (hidden by default)
	const showFailedInspectionsBanner = useFeatureEnabled('evir-px-failed-inspections-banner', false);

	// Filter to only failed inspections
	const failedInspections = useMemo(() => {
		return inspections.filter((insp) => insp.failed === true);
	}, [inspections]);

	// Group by failure reason
	const groupedFailures = useMemo(() => {
		return groupByReason(failedInspections);
	}, [failedInspections]);

	const failedCount = failedInspections.length;
	const uniqueIssuesCount = groupedFailures.length;

	// Don't render if feature flag is disabled or no failed inspections
	if (!showFailedInspectionsBanner || failedCount === 0) {
		return null;
	}

	const handleToggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	const handleFixAll = (group) => {
		if (onFixAll) {
			onFixAll(group.inspections);
		}
	};

	return (
		<div className="failed-inspections-banner">
			<div
				className="banner-header"
				onClick={handleToggleExpand}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => e.key === 'Enter' && handleToggleExpand()}
			>
				<div className="banner-header-content">
					<WarningAmber className="warning-icon" />
					<div className="banner-text">
						<span className="banner-title">
							{failedCount} {messages['evirPx_failed_inspections_detected'] || 'Failed Inspections Detected'}
						</span>
						<span className="banner-subtitle">
							{uniqueIssuesCount} {messages['evirPx_unique_issues_bulk_fix'] || 'unique issues can be fixed in bulk'}
						</span>
					</div>
				</div>
				<div className="banner-expand-icon">
					{isExpanded ? <ExpandMore /> : <ChevronRight />}
				</div>
			</div>

			{isExpanded && (
				<div className="banner-details">
					{groupedFailures.map((group, index) => (
						<div key={index} className="failure-group">
							<div className="failure-group-header">
								<span className="inspection-count-badge">
									{group.count} {group.count === 1
										? (messages['evirPx_inspection'] || 'inspection')
										: (messages['evirPx_inspections'] || 'inspections')}
								</span>
								<span className="affected-text">
									{messages['evirPx_affected_by_issue'] || 'affected by this issue'}
								</span>
							</div>
							<div className="failure-reason">
								{group.reason}
							</div>
							<div className="affected-assets">
								{group.inspections.slice(0, 5).map((insp, idx) => (
									<span key={idx} className="asset-tag">
										{insp.assetViewModel?.firstAssetName || insp.id}
									</span>
								))}
								{group.inspections.length > 5 && (
									<span className="more-assets">
										+{group.inspections.length - 5} more
									</span>
								)}
							</div>
							<button
								className="fix-all-button"
								onClick={() => handleFixAll(group)}
							>
								<Build className="fix-icon" />
								{messages['evirPx_fix_all'] || 'Fix All'} ({group.count})
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default FailedInspectionsBanner;
